import { useMemo, useState, useEffect } from "react";
import Alert from "react-bootstrap/Alert";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";

import {
    AXNUIContent,
    AXNUILayout
} from "@iddataweb/idw-common-ui/types";
import {
    isStringTrue
} from "@iddataweb/idw-common-ui/utils";
import { DISPLAY_RESULTS_PROPERTY_TYPE_ID } from "@constants/landing-page-constants";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { ThemingLandingPageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { RadioInputFormGroup } from "@containers/common/radio-input-form-group/radio-input-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { LandingPageSectionsContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-landing-page-tab-container/landing-page-sections-container/landing-page-sections-container";
import { RedirectUrlInformation } from "@idw-types/redirect-url-information";
import { RedirectUrlOriginTypes } from "@enums/redirect-url-origin-types";

type ThemingLandingPageTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingLandingPageValidationErrors;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    isDisplayResultsEnabled: boolean;
    inheritedIsDisplayResultsEnabled: boolean;
    redirectUrlsInformation: RedirectUrlInformation;
    isServiceBelongToWorkflow: boolean;
    pageLevel: PageLevelTypes;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes, previewableRedirectUrls?: Array<string>) => React.ReactNode;
    handleRemoveRedirect: (index: number, redirectUrlOrigin: RedirectUrlOriginTypes) => void;
    handleAddRedirect: (newRedirectUrl: string) => void;
    handleRedirectChangeAtIndex: (newRedirectUrl: string, index: number, redirectUrl: string, redirectUrlOrigin: RedirectUrlOriginTypes) => void;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUILayoutPropertyValue: (axnuiLayout: AXNUILayout) => void;
};

export const ThemingLandingPageTabContainer = (props: ThemingLandingPageTabContainerProps) => {
    const {
        isDisabled,
        isServiceBelongToWorkflow,
        validationErrors,
        axnuiContent,
        axnuiLayout,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        isDisplayResultsEnabled,
        inheritedIsDisplayResultsEnabled,
        pageLevel,
        redirectUrlsInformation,
        generatePreviewComponent,
        handleAddRedirect,
        handleRemoveRedirect,
        handleRedirectChangeAtIndex,
        updateProperties,
        updateAXNUIContentPropertyValue,
        updateAXNUILayoutPropertyValue
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        customRedirectUrlsWithOriginalIndex,
        previewableRedirectUrls
    } = redirectUrlsInformation;

    const [selectedCustomLandingPage, setSelectedCustomLandingPage] = useState(customRedirectUrlsWithOriginalIndex.length > 0 ? customRedirectUrlsWithOriginalIndex[0] : null);

    useEffect(() => {
        if (customRedirectUrlsWithOriginalIndex.length > 0) {
            //if we have more than 1 redirect url and there is a selected custom redirect url
            if (selectedCustomLandingPage) {
                //check if the selected url still exists
                const previouslySelectedCustomLandingPage = customRedirectUrlsWithOriginalIndex.find(lp => lp.index === selectedCustomLandingPage.index);

                //if the previously selected landing page no longer exists, set current selected to first in list
                if (!previouslySelectedCustomLandingPage) {
                    setSelectedCustomLandingPage(customRedirectUrlsWithOriginalIndex[0]);
                }
                //if the url at the index is different, we need to find the correct index
                else if (previouslySelectedCustomLandingPage.url !== selectedCustomLandingPage.url) {
                    const positionWithSameUrl = customRedirectUrlsWithOriginalIndex.find(lp => lp.url === selectedCustomLandingPage.url);
                    //if there is a matching url at some index, set the selected url to object at that index
                    if (positionWithSameUrl) {
                        setSelectedCustomLandingPage(positionWithSameUrl);
                    } else {
                        //there is no matching url - set selected to first object in list
                        setSelectedCustomLandingPage(customRedirectUrlsWithOriginalIndex[0]);
                    }
                }
            } else {
                //there is no selected landing page - set it to first object in list 
                setSelectedCustomLandingPage(customRedirectUrlsWithOriginalIndex[0]);
            }
        } else {
            //there are no urls, set selected to null
            setSelectedCustomLandingPage(null);
        }
    }, [customRedirectUrlsWithOriginalIndex]);

    const handleDisplayResultsChange = (newValue: string) => {
        if (isStringTrue(newValue)) {
            updateProperties([
                { propertyTypeId: DISPLAY_RESULTS_PROPERTY_TYPE_ID, newValue: "true", added: true }
            ]);
        } else {
            updateProperties([
                { propertyTypeId: DISPLAY_RESULTS_PROPERTY_TYPE_ID, newValue: null, added: false }
            ]);
        }
    };

    const handleResetDisplayResults = () => {
        updateProperties([
            { propertyTypeId: DISPLAY_RESULTS_PROPERTY_TYPE_ID, newValue: null, added: false }
        ]);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.LANDING_PAGE, previewableRedirectUrls);
    }, [generatePreviewComponent, previewableRedirectUrls]);

    const resource = resources.pages.verificationServiceDetails.tabs.theming.subTabs.landingPage;

    return (
        <div>
            <ContentPageHeader title={resource.title} />
            <ContentPageBody isMaxWidth={true}>
                {(pageLevel === PageLevelTypes.APIKEY && isServiceBelongToWorkflow) ? (
                    <Alert variant={"warning"}>{resource.serviceDescription}</Alert>
                ) : (
                    <ThemingSubHeaderContainer description={resource.workflowDescription} />
                )}
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedIsDisplayResultsEnabled}
                            currentThemingPropertyValue={isDisplayResultsEnabled}
                            pageLevel={pageLevel}
                            hideComparison={pageLevel === PageLevelTypes.WORKFLOW}
                        >
                            <RadioInputFormGroup
                                controlId={`theming-landing-page-use-landing-page-radio-input`}
                                automationId={`theming-landing-page-use-landing-page-radio-input`}
                                label={resource.inputs.useLandingPage.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleDisplayResultsChange}
                                options={BOOLEAN_RADIO_INPUT_OPTIONS}
                                value={isDisplayResultsEnabled ? "true" : "false"}
                                disabled={isDisabled || isServiceBelongToWorkflow}
                                isRequired={true}
                                afterLabelTooltipText={resource.inputs.useLandingPage.tooltip}
                                afterLabelIcon={(isDisabled || isServiceBelongToWorkflow) ? null : faUndo}
                                afterLabelIconOnClick={handleResetDisplayResults}
                            />
                        </ThemingFormGroupWrapper>
                        <LandingPageSectionsContainer
                            isDisabled={isDisabled || isServiceBelongToWorkflow || !isDisplayResultsEnabled}
                            isTheming={isDisplayResultsEnabled}
                            showOriginBadge={false}
                            validationErrors={validationErrors}
                            axnuiContent={axnuiContent}
                            axnuiLayout={axnuiLayout}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            inheritedAXNUILayout={inheritedAXNUILayout}
                            pageLevel={pageLevel}
                            redirectUrlsInformation={redirectUrlsInformation}
                            handleRemoveRedirect={handleRemoveRedirect}
                            handleAddRedirect={handleAddRedirect}
                            handleRedirectChangeAtIndex={handleRedirectChangeAtIndex}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                            updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        />
                    </ThemingFormContainer>
                    {isDisplayResultsEnabled && (previewComponent)}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div >
    )
}